import { apiService } from './api';
import { QuestionType, QuestionImportance } from './questionPaperService';
import type { NumberingStyle, MCQOptionStyle, MarksDisplayMode } from '../utils/paperNumbering';

export enum PaperStatus {
  DRAFT = 'Draft',
  PUBLISHED = 'Published',
  ARCHIVED = 'Archived'
}

/** Per-paper numbering config — matches backend paperNumbering util. */
export interface PaperNumbering {
  sectionStyle: NumberingStyle;
  subSectionStyle: NumberingStyle;
  questionStyle: NumberingStyle;
  subPartStyle: NumberingStyle;
  mcqOptionStyle: MCQOptionStyle;
}

/** Section-level "Attempt any N of M" rule. Omit for "attempt all". */
export interface SectionSelectionRule {
  attemptCount: number;
  totalCount: number;
}

export interface GeneratedQuestionPaper {
  _id: string;
  schoolId: string | { _id: string; name: string };
  classId: string | { _id: string; name: string };
  subjectId: string | { _id: string; name: string };
  title: string;
  description?: string;
  instructions?: string;
  totalMarks: number;
  duration: number;
  /** ISO date string of the scheduled exam — printed on the PDF header. */
  examDate?: string;
  textDirection?: 'ltr' | 'rtl';
  marksDisplayMode?: MarksDisplayMode;
  numbering?: PaperNumbering;
  status: PaperStatus;
  createdBy: string | { _id: string; name: string; email: string };
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface QuestionPaperSection {
  _id?: string;
  /** Client-side stable key for sections not yet persisted — used so
   *  subsections can reference their parent before it has a DB id. */
  tempKey?: string;
  questionPaperId?: string;
  name: string;
  description?: string;
  instructions?: string;
  order: number;
  /** Optional Phase-1 fields. */
  marks?: number;
  selectionRule?: SectionSelectionRule;
  maxMarks?: number;
  minQuestions?: number;
  maxQuestions?: number;
  questions?: QuestionPaperQuestion[];
  /** Legacy boolean — superseded by `numberingMode`. Kept for backwards
   *  read-compat with rows written before the three-way control. */
  numberingContinues?: boolean;
  /**
   * How this section's question counter starts:
   *   • 'restart'  → 1 (default)
   *   • 'continue' → previous section's end + 1
   *   • 'startAt'  → `numberingStartAt`
   */
  numberingMode?: 'restart' | 'continue' | 'startAt';
  numberingStartAt?: number;
  /** Per-section override for question numbering style. Empty/undefined
   *  falls back to the paper-wide setting. */
  questionStyleOverride?: NumberingStyle;
  subPartStyleOverride?: NumberingStyle;
  /** Parent section reference — set on subsections. May be a real _id (for
   *  saved parents) or a `tempKey` (for parents created in the same session). */
  parentSectionId?: string;
  parentSectionKey?: string;
}

/**
 * A paper row.
 *
 * Snapshot model: every renderable field lives inline on the row. `questionId`
 * is a **soft** breadcrumb — set when the row was sourced from the bank,
 * null when it was authored inline. It is never dereferenced on render; its
 * only purpose is the "Sync from bank" action (see `resyncQuestionFromBank`).
 *
 * There is no `isCustom` flag anymore — infer it from `!questionId`.
 */
export interface QuestionPaperQuestion {
  _id?: string;
  sectionId?: string;
  /** Soft ref to the bank Question this row was sourced from. Null / undef
   * for inline-authored questions. Never populated. */
  questionId?: string;

  // ─── Snapshot fields (frozen at add-time; refreshed only by explicit
  //     "Sync from bank"). ────────────────────────────────────────────────
  questionText: string;
  type: QuestionType;
  importance: QuestionImportance;
  options?: string[];
  correctAnswer?: string;
  answer?: string;
  answerImages?: string[];
  questionImages?: string[];
  subParts?: { text: string }[];

  // ─── Paper-row fields. ────────────────────────────────────────────────
  marks: number;
  order: number;
  chapterId?: string | { _id: string; name: string };
  difficulty?: string;
  /** Questions sharing the same id are alternatives (student picks one). */
  choiceGroupId?: string;
  /** Optional custom label for the OR divider (defaults to "OR"). */
  choiceGroupLabel?: string;
  /** Parent / sub-question grouping — questions sharing this id form one
   *  composite (parent + children). The parent's Q-number is kept; children
   *  render with the paper's sub-part numbering ((i) (ii) (a) etc.). */
  subGroupId?: string;
  subGroupRole?: 'parent' | 'child';
}

export interface GeneratedQuestionPaperFormData {
  classId: string;
  subjectId: string;
  title: string;
  description?: string;
  instructions?: string;
  totalMarks: number;
  duration: number;
  /** ISO date string of the scheduled exam (YYYY-MM-DD). */
  examDate?: string;
  textDirection?: 'ltr' | 'rtl';
  marksDisplayMode?: MarksDisplayMode;
  numbering?: PaperNumbering;
  sections: QuestionPaperSection[];
}

export interface GeneratedQuestionPaperWithSections {
  paper: GeneratedQuestionPaper;
  sections: QuestionPaperSection[];
}

/** Snapshot-field key emitted by the backend's diff. The FE maps these
 *  to user-friendly labels in `SyncPreviewDialog`. */
export type ResyncChangedField =
  | 'questionText'
  | 'type'
  | 'importance'
  | 'options'
  | 'correctAnswer'
  | 'answer'
  | 'answerImages'
  | 'questionImages'
  | 'subParts';

/** One row of the preview — a question that will be overwritten on apply. */
export interface ResyncChange {
  paperQuestionId: string;
  sectionId: string;
  sectionName: string;
  /** Zero-based position within the section; FE displays `order + 1`. */
  order: number;
  fieldsChanged: ResyncChangedField[];
  /** First ~140 chars of the current snapshot's `questionText`. */
  currentText: string;
  /** First ~140 chars of the bank's `questionText`. */
  newText: string;
}

/** Shared shape for both `resyncAllFromBank` (apply) and
 *  `previewResyncFromBank` (dry-run). `changes` is populated in both
 *  responses; the preview-vs-apply distinction is in `dryRun`. */
export interface ResyncAllResponse {
  updated: number;
  unchanged: number;
  dangling: number;
  totalWithRef: number;
  totalRows: number;
  /** Legacy alias for `updated` — new code should read `updated`. */
  synced: number;
  dryRun: boolean;
  changes: ResyncChange[];
}

export const generateQuestionPaperService = {
  // Get all generated question papers
  getGeneratedQuestionPapers: async (params?: {
    schoolId?: string;
    classId?: string;
    subjectId?: string;
    status?: PaperStatus;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiService.get<{
      papers: GeneratedQuestionPaper[];
      pagination: any;
    }>('/generate-question-paper', params);
    return {
      papers: response.data?.papers || [],
      pagination: response.data?.pagination || {}
    };
  },

  // Get single question paper with all details
  getGeneratedQuestionPaperById: async (id: string) => {
    const response = await apiService.get<GeneratedQuestionPaperWithSections>(
      `/generate-question-paper/${id}`
    );
    return response.data;
  },

  // Create new question paper
  createGeneratedQuestionPaper: async (data: GeneratedQuestionPaperFormData) => {
    const response = await apiService.post<{ paper: GeneratedQuestionPaper; sections?: QuestionPaperSection[] }>(
      '/generate-question-paper',
      data
    );
    return response.data;
  },

  // Update question paper
  updateGeneratedQuestionPaper: async (id: string, data: Partial<GeneratedQuestionPaperFormData> & { status?: PaperStatus }) => {
    const response = await apiService.put<{ paper: GeneratedQuestionPaper; sections?: QuestionPaperSection[] }>(
      `/generate-question-paper/${id}`,
      data
    );
    return response.data;
  },

  // Delete question paper
  deleteGeneratedQuestionPaper: async (id: string) => {
    const response = await apiService.delete(`/generate-question-paper/${id}`);
    return response.data;
  },

  // Duplicate question paper
  duplicateGeneratedQuestionPaper: async (id: string) => {
    const response = await apiService.post<{ paper: GeneratedQuestionPaper }>(
      `/generate-question-paper/${id}/duplicate`
    );
    return response.data?.paper;
  },

  // Save custom question to bank
  saveCustomQuestionToBank: async (questionPaperQuestionId: string) => {
    const response = await apiService.post('/generate-question-paper/save-to-bank', {
      questionPaperQuestionId
    });
    return response.data;
  },

  /**
   * Resync a paper row's snapshot with the latest version from the bank.
   * Overwrites only the snapshot fields (text / options / answers /
   * images / subParts / type / importance) — marks, order, and group
   * membership stay put. Skips the write entirely when bank and paper
   * are already byte-identical, reflected in `changed: false`.
   *
   * Backend responses:
   *   • 200 (changed: true)  → row updated; refreshed row in `data.question`
   *   • 200 (changed: false) → bank already matched; nothing to write
   *   • 400  → this row has no bank source (was authored inline)
   *   • 410  → the bank source has been deleted; row snapshot unchanged
   *   • 404  → paper row not found
   */
  resyncQuestionFromBank: async (paperQuestionId: string) => {
    const response = await apiService.post<{
      question: QuestionPaperQuestion;
      changed: boolean;
    }>(
      `/generate-question-paper/questions/${paperQuestionId}/resync-from-bank`,
    );
    return response.data;
  },

  /**
   * Bulk resync — classify every bank-linked row on this paper. Only rows
   * where the bank version genuinely differs from the current snapshot
   * are written; the rest are counted as `unchanged`. See
   * `resyncAllFromBank` in the controller for the Mongo-op shape.
   *
   * The response always includes a `changes[]` array (empty when there's
   * nothing to update) and the `dryRun` flag echoing the request mode.
   * Use `previewResyncFromBank()` for the no-write preview call.
   */
  resyncAllFromBank: async (paperId: string) => {
    const response = await apiService.post<ResyncAllResponse>(
      `/generate-question-paper/${paperId}/resync-all-from-bank`,
    );
    return response.data;
  },

  /**
   * Dry-run version of `resyncAllFromBank` — classifies every row and
   * returns the full `changes[]` breakdown without writing anything.
   * Used to render the preview dialog before the author confirms the
   * sync. The server guarantees `dryRun: true` on the response so the
   * caller can distinguish from a real apply.
   */
  previewResyncFromBank: async (paperId: string) => {
    const response = await apiService.post<ResyncAllResponse>(
      `/generate-question-paper/${paperId}/resync-all-from-bank?dryRun=true`,
    );
    return response.data;
  },

  /** The draft rendered as the PDF's HTML — what the Review step shows. */
  previewPaper: async (draft: object): Promise<string> => {
    const response = await apiService.post<{ html: string }>('/generate-question-paper/preview', draft);
    return response.data!.html;
  },

  // Export question paper as PDF
  exportQuestionPaperPDF: async (id: string, includeAnswers: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/generate-question-paper/${id}/export/pdf?includeAnswers=${includeAnswers}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      // Get filename from header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'question_paper.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Ensure filename has .pdf extension
      if (!filename.endsWith('.pdf')) {
        filename += '.pdf';
      }

      // Download the file
      const blob = await response.blob();
      
      // Create blob with correct MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      return { success: true };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw error;
    }
  }
};

